import { useState } from "react";
import {
    Box,
    Typography,
    Collapse,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Chip,
    CircularProgress,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

import { useFetchJeopardyTree } from "../hooks/useFetchJeopardyTree";

interface UiControl {
    label: string;
    default_selection: boolean;
    select_color: string;
    unselect_color: string;
}

interface JeopardyItem {
    display_text: string;
    value: string | number;
    filter_code: string;
    item_code: string;
    ui_control: UiControl;
    child_item: JeopardyItem[];
}

interface JeopardyItemProps {
    item: JeopardyItem;
    itemPath: string;
    level: number;
    expanded: { [key: string]: boolean };
    selected: string | null;
    onExpand: (path: string) => void;
    onSelect: (path: string | null) => void;
    isLast: boolean;
}

const JeopardyItem: React.FC<JeopardyItemProps> = ({
    item,
    itemPath,
    level,
    expanded,
    selected,
    onExpand,
    onSelect,
    isLast,
}) => {
    const hasChildren = !!item.child_item && item.child_item.length > 0;
    const isExpanded = expanded[itemPath];
    const isSelected = selected === item.filter_code;

    return (
        <Box>
            <ListItem
                sx={{
                    display: "flex",
                    alignItems: "center",
                    pl: 2 + level * 2,
                    pr: level === 0 ? 1 : 2,
                    justifyContent: "space-between",
                    gap: 1,
                    py: 0.3,
                    cursor: "pointer",
                    bgcolor: isSelected ? "action.selected" : "transparent",
                    borderLeft: isSelected
                        ? `3px solid ${item.ui_control.select_color}`
                        : "3px solid transparent",
                    "&:hover": {
                        bgcolor: "action.hover",
                    },
                }}
                onClick={() => (hasChildren ? onExpand(itemPath) : isSelected ? onSelect(null) : onSelect(item.filter_code))}
            >
                {level === 0 && (
                    <ListItemIcon sx={{ minWidth: 18 }}>
                        <NotificationsNoneIcon
                            fontSize="small"
                            sx={{
                                color: isSelected
                                    ? item.ui_control.select_color
                                    : item.ui_control.unselect_color
                            }}
                        />
                    </ListItemIcon>
                )}
                <ListItemText>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            color={level === 0 ? "text.primary" : "text.secondary"}
                            sx={{
                                fontWeight: isSelected ? 600 : 400,
                            }}
                        >
                            {item.ui_control.label}
                        </Typography>
                        <Chip
                            size="small"
                            variant={isSelected ? "filled" : "outlined"}
                            label={item.value}
                            sx={{
                                ...(isSelected && {
                                    bgcolor: item.ui_control.select_color,
                                    color: "white",
                                    borderColor: item.ui_control.select_color,
                                }),
                            }}
                        />
                    </Box>
                </ListItemText>
                {hasChildren && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onExpand(itemPath);
                        }}
                    >
                        {isExpanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
                    </IconButton>
                )}
            </ListItem>

            {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {item.child_item.map((childItem, childIdx) => (
                            <JeopardyItem
                                key={`${itemPath}-${childIdx}`}
                                item={childItem}
                                itemPath={`${itemPath}-${childIdx}`}
                                level={level + 1}
                                expanded={expanded}
                                selected={selected}
                                onExpand={onExpand}
                                onSelect={onSelect}
                                isLast={childIdx === item.child_item.length - 1}
                            />
                        ))}
                    </List>
                </Collapse>
            )}

            {level === 0 && !isLast && <Divider sx={{ my: 0.5 }} />}
        </Box>
    );
};

export const JeopardyList = ({ selectedFilterCode, onSelectedFilter, treeFilterCode }: { selectedFilterCode: string | null, onSelectedFilter: (filterCode: string | null) => void, treeFilterCode: string | null }) => {

    const { data, isLoading, isError, error } = useFetchJeopardyTree(selectedFilterCode);

    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>({});

    const handleExpand = (path: string) => {
        setExpanded((prev) => ({ ...prev, [path]: !prev[path] }));
    };

    const handleSelect = (path: string | null) => {
        onSelectedFilter(path);
    };

    if (isLoading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
            >
                <CircularProgress />
            </Box>
        );
    };

    if (isError) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
            >
                <Typography variant="body2" color="error">
                    Error loading data
                </Typography>
            </Box>
        );
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            height="100%"
            overflow="hidden"
        >
            <List
                disablePadding
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    py: 1
                }}
            >
                {data.map((item: JeopardyItem, idx: number) => (
                    <JeopardyItem
                        key={`root-${idx}`}
                        item={item}
                        itemPath={`root-${idx}`}
                        level={0}
                        expanded={expanded}
                        selected={treeFilterCode}
                        onExpand={handleExpand}
                        onSelect={handleSelect}
                        isLast={idx === data.length - 1}
                    />
                ))}
            </List>
        </Box>
    );
};